import {Poi} from '../../react-app-env';
import React, {ReactNode} from 'react';
import ListViewGroup from '../listview/listviewgroup/ListViewGroup';
import {i18n} from '../../i18n/i18n';
import ListViewItem from '../listview/listviewitem/ListViewItem';
import {noop, openLink} from '../../utils/Utils';
import {PoiMetaData} from '../../services/MetaDataService';
import {canHandleTelLinks} from '../../utils/Device';
import {delay} from '../../utils/Timing';
import ListViewSplitItem from '../listview/listviewitem/ListViewSplitItem';
import {DefaultItemDecorations} from '../listview/listviewitem/ListViewItem.types';
import {Events, EventType} from '../../events/Events';
import ListView from '../listview/ListView';
import {PopupMenuViewArgs} from '../popupmenu/PopupMenuView';
import {Coordinates} from '../../types/device.types';
import {getViewportCoordinatesForEvent} from '../../utils/Viewport';

export interface LinksGroupProps {
  poi: Poi;
  metaData: PoiMetaData | null;
}

const LinksGroup: React.FunctionComponent<LinksGroupProps> = ({ poi, metaData }) => {

  const [phoneCopyConfirmationVisible, setPhoneCopyConfirmationVisible] = React.useState(false);
  const [websiteCopyConfirmationVisible, setWebsiteCopyConfirmationVisible] = React.useState(false);

  /**
   * Copies the phone number to the clipboard and shows a confirmation message.
   */
  async function copyPhoneNumberToClipboard() {
    await navigator.clipboard.writeText(metaData?.phoneNumber!);
    setPhoneCopyConfirmationVisible(true);
    delay(2500, () => setPhoneCopyConfirmationVisible(false));
  }

  /**
   * Copies the website link to the clipboard and shows a confirmation message.
   */
  async function copyWebsiteLinkToClipboard() {
    await navigator.clipboard.writeText(metaData?.website!);
    setWebsiteCopyConfirmationVisible(true);
    delay(2500, () => setWebsiteCopyConfirmationVisible(false));
  }

  /**
   * Opens the phone dialer with the POI's phone number.
   */
  function dialPhoneNumber() {
    openLink('tel:' + metaData?.phoneNumber, '_self');
  }

  /**
   * Returns whether a custom link can be added (max 5 links).
   */
  function canAddCustomLink(): boolean {
    return poi.links.length < 5;
  }

  /**
   * Shows the popup menu for custom link options.
   *
   * @param {string} link Link URL
   * @param {number} index Index of the link
   * @param {object} origin Optional origin coordinates for the popup menu.
   */
  function showCustomLinkOptions(link: string, index: number, origin?: Coordinates) {
    Events.publish(EventType.POPUPMENU_OPEN, {
      origin: origin,
      node:   <React.Fragment>
                <ListViewItem title={i18n().poiDetails.links.customLinkOptions.copyToClipboard}
                              onClick={() => {
                                navigator.clipboard.writeText(link);
                                Events.publish(EventType.POPUPMENU_CLOSE);
                              }}
                              iconCategory="fa-copy"/>
                <ListViewItem title={i18n().poiDetails.links.customLinkOptions.editLink}
                              onClick={noop}
                              iconCategory="fa-edit"/>
                <ListViewItem title={i18n().poiDetails.links.customLinkOptions.deleteLink}
                              onClick={noop}
                              iconCategory="fa-trash"/>
              </React.Fragment>
    } as PopupMenuViewArgs);
  }

  /**
   * Renders a custom link.
   *
   * @param {string} link The link URL.
   * @param {number} index The index of the link in the list.
   * @return {React.ReactNode}
   */
  function renderCustomLink(link: string, index: number): ReactNode {
    try {
      const url = new URL(link);

      return <ListViewSplitItem
          key={link}
          primaryButton={{
            title:        url.host,
            ariaLabel:    i18n().poiDetails.links.openLink + ': ' + url.host,
            iconCategory: 'fa-link',
            decoration:   DefaultItemDecorations.EXTERNAL_LINK,
            onClick:      () => onCustomLinkClicked(link, index)
          }}
          secondaryButton={{
            decoration: DefaultItemDecorations.POPUPMENU,
            onClick:    (event: React.UIEvent) => {
              showCustomLinkOptions(link, index, getViewportCoordinatesForEvent(event));
            }
          }}
      />;
    } catch(ex) {
      console.warn('Invalid link for POI:', link, ex);
      return null;
    }
  }

  /**
   * Renders the website link if available.
   * @return {React.ReactNode}
   */
  function renderWebsiteLink(): ReactNode {
    const website = metaData?.website;
    if (!website) {
      return null;
    }

    try {
      const url = new URL(website);
      return <ListViewSplitItem
          key={website}
          confirmationMessage={websiteCopyConfirmationVisible
              ? i18n().poiDetails.links.copyWebsiteConfirmation
              : undefined}
          primaryButton={{
            title:        url.host,
            ariaLabel:    i18n().poiDetails.links.openLink + ': ' + url.host,
            iconCategory: 'fa-globe',
            decoration:   DefaultItemDecorations.EXTERNAL_LINK,
            onClick:      () => onWebsiteLinkClicked()
          }}
          secondaryButton={{
            decoration: DefaultItemDecorations.COPY,
            onClick:    () => copyWebsiteLinkToClipboard()
          }}
      />;
    } catch(ex) {
      console.warn('Invalid website metadata for POI:', website, ex);
      return null;
    }
  }

  /**
   * Renders a phone link item, which either opens the dialer or copies the number to clipboard.
   *
   * @return {React.ReactNode} The rendered phone link item.
   */
  function renderPhoneLink(): ReactNode {
    const phoneNumber = metaData?.phoneNumber;
    if (!phoneNumber) {
      return null;
    }

    if (canHandleTelLinks()) {
      return <ListViewItem key={phoneNumber}
                           title={phoneNumber}
                           ariaLabel={i18n().poiDetails.links.callPhoneNumber + ': ' + phoneNumber}
                           iconCategory="fa-phone"
                           decoration={DefaultItemDecorations.EXTERNAL_LINK}
                           onClick={() => dialPhoneNumber()}/>;
    } else {
      return <ListViewItem key={phoneNumber}
                           title={phoneNumber}
                           ariaLabel={i18n().poiDetails.links.callPhoneNumber + ': ' + phoneNumber}
                           iconCategory="fa-phone"
                           decoration={DefaultItemDecorations.COPY}
                           confirmationMessage={phoneCopyConfirmationVisible
                               ? i18n().poiDetails.links.copyPhoneConfirmation
                               : undefined}
                           onClick={() => copyPhoneNumberToClipboard()}/>;
    }
  }

  /**
   * Handles the click event for the website link.
   */
  function onWebsiteLinkClicked() {
    const link = metaData?.website;
    if (link) {
      const url = new URL(link);
      openLink(url.href);
    }
  }

  /**
   * Handles the click event for a custom link.
   *
   * @param {string} link URL of the link
   * @param {number} index Index of the link
   */
  function onCustomLinkClicked(link: string, index: number) {
    const url = new URL(link);
    openLink(url.href);
  }

  return <ListViewGroup>
    {renderPhoneLink()}
    {renderWebsiteLink()}
    {poi.links.map((item, index) => renderCustomLink(item, index))}
    {canAddCustomLink() && (
        <ListViewItem title={i18n().poiDetails.links.addLink}
                      iconCategory="fa-plus"
                      decoration={DefaultItemDecorations.EDIT}
                      onClick={() => noop()}/>
    )}
  </ListViewGroup>;
};

export default LinksGroup;
